import { createContext, useContext, useReducer, useCallback, useMemo } from 'react';
import { SnowballCalculationService } from '../services/snowball-calculation.service';

// Action types
const SNOWBALL_ACTIONS = {
    SET_STARTER_LUMP: 'SET_STARTER_LUMP',
    SET_DEBIT_ACCOUNT: 'SET_DEBIT_ACCOUNT',
    SET_MASTER_START_DELAY: 'SET_MASTER_START_DELAY',
    SET_ACCOUNTS: 'SET_ACCOUNTS',
    SET_TRANSACTIONS: 'SET_TRANSACTIONS',
    SET_AGGREGATE_RESULTS: 'SET_AGGREGATE_RESULTS',
    SET_EXISTING_TRANSACTION_IDS: 'SET_EXISTING_TRANSACTION_IDS',
    SET_OVERLAYS: 'SET_OVERLAYS',
    SET_AUTO_CREATE: 'SET_AUTO_CREATE',
    CALCULATE_SCHEDULES: 'CALCULATE_SCHEDULES',
    UPDATE_ACCOUNT: 'UPDATE_ACCOUNT',
    RESET_CALCULATIONS: 'RESET_CALCULATIONS'
};

// Initial state
const initialState = {
    snowballStarterLump: 0,
    debitAccountID: null,
    masterStartDelayMonths: 0,
    accounts: [],
    transactions: [],
    aggregateResults: null,
    existingTransactionIds: null,
    showExistingTransactionsOverlay: false,
    autoCreate: true,
    isCalculating: false,
    calculationError: null
};

// Reducer function
function snowballReducer(state, action) {
    switch (action.type) {
        case SNOWBALL_ACTIONS.SET_STARTER_LUMP:
            return { ...state, snowballStarterLump: action.payload };
            
        case SNOWBALL_ACTIONS.SET_DEBIT_ACCOUNT:
            return { ...state, debitAccountID: action.payload };
            
        case SNOWBALL_ACTIONS.SET_MASTER_START_DELAY:
            return { ...state, masterStartDelayMonths: action.payload };
            
        case SNOWBALL_ACTIONS.SET_ACCOUNTS:
            return { ...state, accounts: action.payload };
            
        case SNOWBALL_ACTIONS.SET_TRANSACTIONS:
            return { ...state, transactions: action.payload };
            
        case SNOWBALL_ACTIONS.SET_AGGREGATE_RESULTS:
            return { ...state, aggregateResults: action.payload };
            
        case SNOWBALL_ACTIONS.SET_EXISTING_TRANSACTION_IDS:
            return { ...state, existingTransactionIds: action.payload };
            
        case SNOWBALL_ACTIONS.SET_OVERLAYS:
            return {
                ...state,
                showExistingTransactionsOverlay: action.payload.existingTransactions ?? state.showExistingTransactionsOverlay
            };
            
        case SNOWBALL_ACTIONS.SET_AUTO_CREATE:
            return { ...state, autoCreate: action.payload };
            
        case SNOWBALL_ACTIONS.CALCULATE_SCHEDULES:
            const { accounts, applySort = false } = action.payload;
            
            // Apply master start delay to accounts that don't have individual delays
            const accountsWithMasterDelay = accounts.map(account => ({
                ...account,
                startDateDelayMonths: account.startDateDelayMonths ?? state.masterStartDelayMonths
            }));
            
            const calculationResult = SnowballCalculationService.createCalculationContext(
                accountsWithMasterDelay,
                state.snowballStarterLump,
                state.debitAccountID,
                applySort
            );
            
            return {
                ...state,
                accounts: calculationResult.accounts,
                transactions: calculationResult.transactions,
                aggregateResults: calculationResult.aggregateResults,
                isCalculating: false,
                calculationError: calculationResult.isValid ? null : 'Calculation resulted in invalid payment schedules'
            };
            
        case SNOWBALL_ACTIONS.UPDATE_ACCOUNT:
            const updatedAccounts = [...state.accounts];
            const accountIndex = updatedAccounts.findIndex(acc => acc.id === action.payload.id);
            
            if (accountIndex !== -1) {
                updatedAccounts[accountIndex] = action.payload;
                
                // Apply master start delay to accounts that don't have individual delays
                const accountsWithMasterDelay = updatedAccounts.map(account => ({
                    ...account,
                    startDateDelayMonths: account.startDateDelayMonths ?? state.masterStartDelayMonths
                }));
                
                // Recalculate schedules with updated account
                const recalcResult = SnowballCalculationService.createCalculationContext(
                    accountsWithMasterDelay,
                    state.snowballStarterLump,
                    state.debitAccountID,
                    true // Always sort when updating individual accounts
                );
                
                return {
                    ...state,
                    accounts: recalcResult.accounts,
                    transactions: recalcResult.transactions,
                    aggregateResults: recalcResult.aggregateResults
                };
            }
            
            return state;
            
        case SNOWBALL_ACTIONS.RESET_CALCULATIONS:
            return {
                ...state,
                transactions: [],
                aggregateResults: null,
                calculationError: null,
                accounts: state.accounts.map(account => ({
                    ...account,
                    paymentSchedule: null,
                    snowballData: null
                }))
            };
            
        default:
            return state;
    }
}

// Context
const SnowballContext = createContext(null);

// Provider component
export const SnowballProvider = ({ children }) => {
    const [state, dispatch] = useReducer(snowballReducer, initialState);

    // Action creators
    const setSnowballStarterLump = useCallback((amount) => {
        dispatch({ type: SNOWBALL_ACTIONS.SET_STARTER_LUMP, payload: amount });
    }, []);

    const setDebitAccountID = useCallback((accountId) => {
        dispatch({ type: SNOWBALL_ACTIONS.SET_DEBIT_ACCOUNT, payload: accountId });
    }, []);

    const setMasterStartDelayMonths = useCallback((months) => {
        dispatch({ type: SNOWBALL_ACTIONS.SET_MASTER_START_DELAY, payload: months });
    }, []);

    const setAccounts = useCallback((accounts) => {
        dispatch({ type: SNOWBALL_ACTIONS.SET_ACCOUNTS, payload: accounts });
    }, []);

    const setAggregateResults = useCallback((results) => {
        dispatch({ type: SNOWBALL_ACTIONS.SET_AGGREGATE_RESULTS, payload: results });
    }, []);

    const setExistingTransactionIds = useCallback((ids) => {
        dispatch({ type: SNOWBALL_ACTIONS.SET_EXISTING_TRANSACTION_IDS, payload: ids });
    }, []);

    const setOverlays = useCallback((overlays) => {
        dispatch({ type: SNOWBALL_ACTIONS.SET_OVERLAYS, payload: overlays });
    }, []);

    const setAutoCreate = useCallback((autoCreate) => {
        dispatch({ type: SNOWBALL_ACTIONS.SET_AUTO_CREATE, payload: autoCreate });
    }, []);

    const calculateSchedules = useCallback((accounts, applySort = true) => {
        if (!SnowballCalculationService.canCalculateSchedules(accounts, state.debitAccountID)) {
            console.warn('Cannot calculate schedules: missing required data');
            return;
        }
        
        dispatch({ 
            type: SNOWBALL_ACTIONS.CALCULATE_SCHEDULES, 
            payload: { accounts, applySort } 
        });
    }, [state.debitAccountID]);

    const updateAccount = useCallback((account) => {
        dispatch({ type: SNOWBALL_ACTIONS.UPDATE_ACCOUNT, payload: account });
    }, []);

    const resetCalculations = useCallback(() => {
        dispatch({ type: SNOWBALL_ACTIONS.RESET_CALCULATIONS });
    }, []);

    // Computed values
    const computedValues = useMemo(() => {
        const hasAccounts = state.accounts && state.accounts.length > 0;
        const hasTransactions = state.transactions && state.transactions.length > 0;
        const hasExistingTransactions = state.existingTransactionIds && state.existingTransactionIds.length > 0;
        const canCalculate = SnowballCalculationService.canCalculateSchedules(state.accounts, state.debitAccountID);
        const allAccountsHaveSchedules = SnowballCalculationService.validateAccountSchedules(state.accounts);

        return {
            hasAccounts,
            hasTransactions,
            hasExistingTransactions,
            canCalculate,
            allAccountsHaveSchedules,
            canCreateTransactions: hasTransactions && state.debitAccountID && !hasExistingTransactions
        };
    }, [state.accounts, state.transactions, state.debitAccountID, state.existingTransactionIds]);

    const contextValue = {
        // State
        ...state,
        ...computedValues,
        
        // Actions
        setSnowballStarterLump,
        setDebitAccountID,
        setMasterStartDelayMonths,
        setAccounts,
        setAggregateResults,
        setExistingTransactionIds,
        setOverlays,
        setAutoCreate,
        calculateSchedules,
        updateAccount,
        resetCalculations
    };

    return (
        <SnowballContext.Provider value={contextValue}>
            {children}
        </SnowballContext.Provider>
    );
};

// Custom hook to use the context
export const useSnowballContext = () => {
    const context = useContext(SnowballContext);
    if (!context) {
        throw new Error('useSnowballContext must be used within a SnowballProvider');
    }
    return context;
};

export { SNOWBALL_ACTIONS };